export type ConfigFieldKind = 'group' | 'array' | 'text' | 'number' | 'boolean' | 'secret';

export interface ConfigField {
  id: string;
  kind: ConfigFieldKind;
  label: string;
  path: Array<string | number>;
  value: string | number | boolean | null;
  children: ConfigField[];
  removable: boolean;
}

export interface ConfigListChange {
  op: 'add' | 'remove';
  path: Array<string | number>;
}

const SECRET = '***';

export function fieldsFromJson(value: unknown): ConfigField[] {
  if (value === null || typeof value !== 'object') {
    return [];
  }
  return childrenOf(value, []);
}

export function applyFieldValue(
  root: unknown,
  path: Array<string | number>,
  raw: string | number | boolean | null,
  kind: ConfigFieldKind,
): unknown {
  const clone = structuredClone(root);
  const parent = parentAt(clone, path);
  const key = path[path.length - 1];
  const next = coerce(raw, kind, readAt(clone, path));
  if (Array.isArray(parent) && typeof key === 'number') {
    parent[key] = next;
  } else if (parent && typeof parent === 'object' && typeof key === 'string') {
    (parent as Record<string, unknown>)[key] = next;
  }
  return clone;
}

export function appendArrayItem(root: unknown, path: Array<string | number>): unknown {
  const clone = structuredClone(root);
  const list = readAt(clone, path);
  if (!Array.isArray(list)) {
    return clone;
  }
  const sample = list.length ? list[list.length - 1] : '';
  list.push(blankValue(sample));
  return clone;
}

export function removeArrayItem(root: unknown, path: Array<string | number>): unknown {
  const clone = structuredClone(root);
  const index = path[path.length - 1];
  const list = parentAt(clone, path);
  if (!Array.isArray(list) || typeof index !== 'number') {
    return clone;
  }
  list.splice(index, 1);
  return clone;
}

function childrenOf(value: unknown, path: Array<string | number>): ConfigField[] {
  if (Array.isArray(value)) {
    return value.map((item, index) => {
      const itemPath = [...path, index];
      const node = nodeFor(item, '', itemPath, true);
      if (node.kind === 'group' || node.kind === 'array') {
        return node;
      }
      return {
        id: `${node.id}.item`,
        kind: 'group' as const,
        label: '',
        path: itemPath,
        value: null,
        children: [{ ...node, removable: false }],
        removable: true,
      };
    });
  }
  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).map(([key, item]) =>
      nodeFor(item, key, [...path, key], false),
    );
  }
  return [];
}

function nodeFor(
  value: unknown,
  label: string,
  path: Array<string | number>,
  removable: boolean,
): ConfigField {
  const id = path.map(part => String(part)).join('.');
  if (Array.isArray(value)) {
    return { id, kind: 'array', label, path, value: null, children: childrenOf(value, path), removable };
  }
  if (value !== null && typeof value === 'object') {
    return { id, kind: 'group', label, path, value: null, children: childrenOf(value, path), removable };
  }
  if (typeof value === 'boolean') {
    return { id, kind: 'boolean', label, path, value, children: [], removable };
  }
  if (typeof value === 'number') {
    return { id, kind: 'number', label, path, value, children: [], removable };
  }
  if (value === SECRET) {
    return { id, kind: 'secret', label, path, value, children: [], removable };
  }
  return {
    id,
    kind: 'text',
    label,
    path,
    value: value == null ? '' : String(value),
    children: [],
    removable,
  };
}

function blankValue(sample: unknown): unknown {
  if (Array.isArray(sample)) {
    return sample.map(item => blankValue(item));
  }
  if (sample && typeof sample === 'object') {
    const copy: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(sample as Record<string, unknown>)) {
      copy[key] = blankValue(item);
    }
    return copy;
  }
  if (typeof sample === 'boolean') {
    return false;
  }
  if (typeof sample === 'number') {
    return 0;
  }
  if (sample === null) {
    return null;
  }
  return '';
}

function coerce(
  raw: string | number | boolean | null,
  kind: ConfigFieldKind,
  previous: unknown,
): unknown {
  if (kind === 'boolean') {
    return raw === true;
  }
  if (kind === 'number') {
    const parsed = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(parsed) ? parsed : previous;
  }
  if (kind === 'secret' && (raw === SECRET || raw === '' || raw == null)) {
    return SECRET;
  }
  if (previous === null && (raw === '' || raw == null)) {
    return null;
  }
  return raw == null ? '' : String(raw);
}

function parentAt(root: unknown, path: Array<string | number>): unknown {
  let cursor = root;
  for (const part of path.slice(0, -1)) {
    if (cursor == null || typeof cursor !== 'object') {
      return null;
    }
    cursor = (cursor as Record<string, unknown>)[part as string];
  }
  return cursor;
}

function readAt(root: unknown, path: Array<string | number>): unknown {
  let cursor = root;
  for (const part of path) {
    if (cursor == null || typeof cursor !== 'object') {
      return undefined;
    }
    cursor = (cursor as Record<string, unknown>)[part as string];
  }
  return cursor;
}
